import { useState, useRef } from "react";
import { Upload, Trash2, FileText, Film, Presentation, Loader2, Download } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";

const CATEGORIES = [
  { id: "handout", label: "Handout", icon: FileText, description: "Sent to attendees in confirmation + follow-up emails" },
  { id: "slides", label: "Slide Deck", icon: Presentation, description: "Attached to panelist + host invitation emails" },
  { id: "intro-media", label: "Intro Media", icon: Film, description: "Holding slide or video link sent to hosts" },
  { id: "other", label: "Other", icon: FileText, description: "General resource" },
];

export default function ResourcesPanel({ event, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [category, setCategory] = useState("handout");
  const fileRef = useRef(null);

  const resources = event.resources || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // 1. Register resource and get upload SAS URL
      const res = await fetch(`/api/zoom-events/${event.id}/resources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type,
          category,
        }),
      });
      if (!res.ok) throw new Error("Failed to register resource");
      const { uploadUrl } = await res.json();

      // 2. Upload file directly to Azure Blob via SAS
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Upload failed");

      onRefresh();
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (resourceId) => {
    if (!confirm("Remove this resource?")) return;
    setDeleting(resourceId);
    try {
      await fetch(`/api/zoom-events/${event.id}/resources/${resourceId}`, { method: "DELETE" });
      onRefresh();
    } catch {
      alert("Failed to delete resource");
    } finally {
      setDeleting(null);
    }
  };

  const getCategoryInfo = (cat) => CATEGORIES.find((c) => c.id === cat) || CATEGORIES[3];

  return (
    <div>
      {/* Upload controls */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            padding: "6px 10px",
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.text,
            fontSize: 13,
            fontFamily: fonts.body,
          }}
        >
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <Button
          variant="primary"
          style={{ fontSize: 12, padding: "6px 14px" }}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <><Loader2 size={12} className="spin" /> Uploading...</> : <><Upload size={12} /> Upload</>}
        </Button>
        <input
          ref={fileRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleUpload}
          accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.mp4,.mov,.jpg,.jpeg,.png,.gif"
        />
      </div>

      {/* Category description */}
      <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 16 }}>
        {getCategoryInfo(category).description}
      </div>

      {/* Resource list */}
      {resources.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, color: colors.textSecondary, fontSize: 13 }}>
          No resources uploaded yet. Upload handouts, slide decks, or intro media.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {resources.map((r) => {
            const cat = getCategoryInfo(r.category);
            const Icon = cat.icon;
            return (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                }}
              >
                <Icon size={16} color={colors.textSecondary} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{r.filename}</div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {cat.label} — uploaded {new Date(r.uploadedAt).toLocaleDateString("en-GB")}
                  </div>
                </div>
                <a
                  href={r.downloadUrl}
                  target="_blank"
                  rel="noopener"
                  style={{ color: colors.info, padding: 4 }}
                  title="Download"
                >
                  <Download size={14} />
                </a>
                <button
                  onClick={() => handleDelete(r.id)}
                  disabled={deleting === r.id}
                  style={{
                    background: "none",
                    border: "none",
                    color: colors.danger,
                    cursor: "pointer",
                    padding: 4,
                    opacity: deleting === r.id ? 0.5 : 1,
                  }}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
